import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import { RollRegister, type RollRegisterControls, type RollSubmitOutcome, type RollSubmitState } from "@/components/attendance/RollRegister";
import { useSessionRoll } from "@/components/attendance/use-session-roll";
import type { Blocker, Effect, Part, Read, Result, TaskBox } from "@/components/ui/box.schema";
import type { SessionAttendance } from "@/lib/api/attendance";
import { ApiError } from "@/lib/api/client";
import { checkCommit } from "@/lib/api/commitSeams";
import { useAuth } from "@/contexts/AuthContext";
import { courseUnitsApi, type CourseUnit } from "@/lib/api/courseUnits";
import { sessionRecordingsApi, sessionRecordingsQueryKey, type SessionRecordings } from "@/lib/api/session-recordings";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";
import { refreshHomeAfterAct } from "@/lib/home-query-keys";
import { formatInCourseZone } from "@/lib/session-portal";

import type { SessionPlanParts } from "../ClassPlanCard";
import { RecordedClassJourney } from "../RecordedClassJourney";
import { PlanTaskField } from "../SessionPlanFields";
import { closeOwnsEdit, PLAN_FIELDS, planFailureWords, planWords } from "../session-plan-content";
import { sessionRollSummary } from "../session-roll-summary";
import { fieldNames, SESSION_COPY, sessionName } from "./session-copy";


const copy = SESSION_COPY.close;
const RECORD_ACTIONS = ["close_session", "update_roll", "complete_attendance", "take_attendance"];
const RECAP_KEYS: readonly string[] = ["recap", "followUp"];

type Publication = { kind: "confirmed" } | { kind: "failed"; reason: string } | { kind: "uncertain"; operationId: string };

/** A roll read as the kit reads it: never a learner count until it answers (R1, R3). */
function rollRead(roll: ReturnType<typeof useSessionRoll>): Read<SessionAttendance> {
  if (roll.data) return { status: "ready", value: roll.data, readAt: new Date(roll.dataUpdatedAt || Date.now()).toISOString() };
  if (roll.isError) return { status: "failed", sentence: copy.rollFailed, retry: () => void roll.refetch() };
  return { status: "loading" };
}

/** The Recording part's word, from the recording read its body made; until then it claims nothing. */
function recordingState(read: SessionRecordings | undefined): Part["state"] {
  if (!read || read.role !== "teaching") return { class: "quiet", word: copy.recordingUnread };
  const published = read.link?.publication_status === "published" || read.artifacts.some((artifact) => artifact.publication_status === "published");
  if (published) return { class: "done", word: copy.recordingPublished };
  if (read.link || read.artifacts.length) return { class: "draft", word: copy.recordingNotPublished };
  return { class: "quiet", word: copy.noRecording };
}

/**
 * S04 Close: one Task whose parts are Roll, Recap and next steps, and
 * Recording, in that order (O2). Its head owns the state, so the Roll part
 * draws no mark of its own (CASE-030). One commit records the roll under its
 * key, marks the session held and, when chosen, publishes the recap; a
 * failed publication is Partly done and retries only the recap (W1). After the
 * session is held, Update roll reopens it to save roll changes (O7).
 */
export function useCloseBox({
  workspace,
  parts,
  rank,
}: {
  workspace: SessionWorkspace;
  parts: SessionPlanParts;
  rank: number | null;
}): TaskBox<SessionAttendance> | null {
  const { session, course, viewer } = workspace;
  const queryClient = useQueryClient();
  const roll = useSessionRoll(session.id, session.status !== "cancelled");
  // The Recording part reads what its own body read, from the same cache: it never fetches again.
  const { me } = useAuth();
  // Same key and options as that body's read, and never enabled here, so the cache keeps one owner.
  const recordings = useQuery<SessionRecordings>({
    queryKey: sessionRecordingsQueryKey(session.id, `${me?.tenant_id ?? "unknown"}:${me?.user_id ?? "unknown"}`),
    queryFn: () => sessionRecordingsApi.get(session.id),
    retry: false,
    enabled: false,
  });
  const controls = useRef<RollRegisterControls>(null);
  const [rollState, setRollState] = useState<RollSubmitState>({ canSubmit: false, submitting: false });
  const [result, setResult] = useState<Result>({ state: "none" });
  const [theirs, setTheirs] = useState<SessionAttendance | null>(null);
  const [publishChoice, setPublishChoice] = useState<boolean | null>(null);
  const { unit, state } = parts;
  const current = useRef(state);
  current.current = state;

  const name = sessionName(session);
  const total = roll.data?.roster.length ?? 0;
  const held = roll.data?.status === "held" || session.status === "held";
  const canRecord = viewer.allowed_actions.some((action) => RECORD_ACTIONS.includes(action));
  const marked = rollState.markedCount ?? 0;
  const unmarked = rollState.unmarkedCount ?? Math.max(total - marked, 0);
  const changedMarks = rollState.changedCount ?? 0;

  // What publishing would send: the plan fields that differ from what learners read (O5).
  const changed = PLAN_FIELDS.filter(
    (field) => (state?.draft[field.key] ?? planWords(unit?.[field.column])).trim() !== planWords(unit?.publication?.[field.column]).trim(),
  );
  const recapWritten = !!(state?.draft.recap.trim() || state?.draft.followUp.trim());
  const recapChanged = changed.some((field) => RECAP_KEYS.includes(field.key));
  const planReady =
    !!state && !!unit && !state.disabled && !state.busy && !state.pending && !state.refused && !state.storageError
    && unit.can_publish === true && !state.dirty && !state.conflict && !state.comparisonUnavailable && unit.learning_objectives.length > 0;
  const planReason = state?.conflict || state?.comparisonUnavailable
    ? copy.planConflict
    : state?.dirty || state?.busy || state?.pending
      ? copy.planWaits
      : !unit
        ? copy.planMissing
        : unit.can_publish !== true
          ? "The primary instructor publishes guidance. Your draft is saved."
        : unit.learning_objectives.length === 0
          ? copy.planObjective
          : null;
  const publish = changed.length > 0 && (publishChoice ?? (recapChanged && recapWritten));
  const sending = changed.map((field) => field.label.toLocaleLowerCase());
  // Held, the commit either saves roll changes or publishes the plan's changes; never both at once.
  const mode: "close" | "roll" | "publish" = !held ? "close" : changedMarks > 0 || changed.length === 0 ? "roll" : "publish";

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["session-workspace"] });
    void queryClient.invalidateQueries({ queryKey: ["course-home"] });
    void queryClient.invalidateQueries({ queryKey: ["attendance", "course", course.id] });
    void queryClient.invalidateQueries({ queryKey: ["attendance", "session", session.id] });
    void queryClient.invalidateQueries({ queryKey: ["course-units", course.id] });
    refreshHomeAfterAct(queryClient);
  }, [queryClient, course.id, session.id]);

  /** Publishes the saved plan only when it is the version this draft read (R4). */
  const publishPlan = async (snapshot: CourseUnit | null): Promise<Publication> => {
    const owner = current.current;
    if (!snapshot || !owner || owner.dirty || owner.disabled || owner.conflict || owner.acknowledged?.revision !== snapshot.revision)
      return { kind: "failed", reason: copy.planWaits };
    try {
      const latest = (await courseUnitsApi.list(course.id)).find((item) => item.id === snapshot.id);
      if (latest?.published_revision === snapshot.saved_revision) return { kind: "confirmed" };
      if (!latest || latest.saved_revision !== snapshot.saved_revision || latest.revision !== snapshot.revision)
        return { kind: "failed", reason: copy.planConflict };
    } catch {
      // The plan couldn't be read before anything was sent: nothing is uncertain, and the changes are kept.
      return { kind: "failed", reason: copy.planUnread };
    }
    const outcome = await owner.publishOutcome();
    if (outcome.kind === "confirmed") return { kind: "confirmed" };
    if (outcome.kind === "uncertain") return { kind: "uncertain", operationId: outcome.operationId };
    return { kind: "failed", reason: planFailureWords(outcome.reason) };
  };

  const at = () => new Date().toISOString();
  const recordedSummary = (saved: SessionAttendance) => sessionRollSummary(saved.roster, undefined, "unmarked");
  /** The roll's lines in a result: what was recorded, and who was left unrecorded. */
  const rollLines = (saved: SessionAttendance | undefined) => {
    const left = saved ? saved.roster.filter((row) => !row.status).length : 0;
    return [copy.rollLine(saved ? recordedSummary(saved) : ""), ...(left ? [copy.leftUnrecordedLine(left)] : [])];
  };
  const publicationLine = (published: boolean, fields: string[]) =>
    !fields.length ? copy.nothingPublished : published ? copy.publishedLine(capitalise(fieldNames(fields)), total) : copy.privateLine(capitalise(fieldNames(fields)));

  const recapRetry = (snapshot: CourseUnit | null, fields: string[], went: string[], requestId: string): Result => ({
    state: "partial",
    cap: copy.closed(name),
    at: at(),
    went: went.map((sentence) => ({ sentence, ids: [session.id] })),
    failed: [{ sentence: copy.partialFailed(capitalise(fieldNames(fields))), ids: snapshot ? [snapshot.id] : [], fix: "recap" }],
    requestId,
    retry: {
      verb: copy.publishRecap,
      note: copy.retryNote,
      run: async () => {
        const again = await publishPlan(current.current?.acknowledged ?? snapshot);
        refresh();
        const next: Result = again.kind === "confirmed"
          ? { state: "confirmed", cap: copy.closed(name), at: at(), effects: [...went, publicationLine(true, fields)], remains: [copy.recordingRemains] }
          : recapRetry(snapshot, fields, went, requestId);
        setResult(next);
        return next;
      },
    },
  });

  /**
   * Close held the session but its publication's answer never came: the held
   * and roll lines stand, and the recap is unsure, never "not published".
   * Check reads that send's own receipt and never publishes again; a definite
   * no turns into the recap's retry (W1).
   */
  const closePublicationUnknown = (snapshot: CourseUnit | null, fields: string[], went: string[], requestId: string, operationId: string): Result => ({
    state: "partial",
    cap: copy.closed(name),
    at: at(),
    went: went.map((sentence) => ({ sentence, ids: [session.id] })),
    failed: [],
    unsure: [{ sentence: copy.publishUnknown, ids: snapshot ? [snapshot.id] : [] }],
    requestId,
    check: {
      verb: copy.checkPublication,
      note: copy.checkPublicationNote,
      run: async () => {
        let checked: Result;
        try {
          const owner = current.current;
          const operation = owner?.pending?.id === operationId
            ? await owner.reconcile()
            : snapshot ? await courseUnitsApi.reconcile(course.id, snapshot.id, operationId, "publish") : undefined;
          checked = !operation ? closePublicationUnknown(snapshot, fields, went, requestId, operationId)
            : operation.state === "committed"
              ? { state: "confirmed", cap: copy.closed(name), at: at(), effects: [...went, publicationLine(true, fields)], remains: [copy.recordingRemains] }
              : recapRetry(snapshot, fields, went, requestId);
        } catch {
          checked = closePublicationUnknown(snapshot, fields, went, requestId, operationId);
        }
        if (checked.state !== "partial" || !checked.unsure) refresh();
        setResult(checked);
        return checked;
      },
    },
  });

  const unconfirmed = (requestId: string, fields: string[], publishing: boolean): Result => ({
    state: "unknown",
    cap: copy.unknownCap,
    requestId,
    sentence: copy.unknownSentence(name),
    check: {
      verb: copy.check,
      note: copy.checkNote,
      run: async () => {
        let checked: Result;
        try {
          const receipt = await checkCommit(requestId);
          // A definite no: said, with the roll and recap kept for another send.
          if (receipt.state === "not_committed") checked = { state: "refused", cap: copy.notClosed, sentence: copy.closeNotCommitted, draft: "kept" };
          else if (receipt.state === "confirmed") {
            const saved = (await roll.refetch()).data;
            checked = {
              state: "confirmed",
              cap: copy.checked(name),
              at: at(),
              effects: [copy.heldLine(name), ...rollLines(saved), publicationLine(false, publishing ? [] : fields)],
              remains: publishing ? [copy.publishUnknown] : [copy.recordingRemains],
            };
          } else checked = unconfirmed(requestId, fields, publishing);
        } catch {
          checked = unconfirmed(requestId, fields, publishing);
        }
        if (checked.state !== "unknown") refresh();
        setResult(checked);
        return checked;
      },
    },
  });

  /**
   * A held session's publication whose answer never came: Not confirmed, and
   * Check reads that send's own receipt (its operation id), never a later
   * read of the plan, so a publication made since can't answer for it. It
   * never publishes again; a definite no is said with the changes kept (W1).
   */
  const publicationUnknown = (snapshot: CourseUnit | null, fields: string[], operationId: string): Result => ({
    state: "unknown",
    cap: copy.publishUnknownCap,
    requestId: operationId,
    sentence: copy.publishUnknown,
    check: {
      verb: copy.checkPublication,
      note: copy.checkPublicationNote,
      run: async () => {
        let checked: Result;
        try {
          const owner = current.current;
          // The draft still holds this send: its own reconcile settles it there. Otherwise the receipt is read by its id.
          const operation = owner?.pending?.id === operationId
            ? await owner.reconcile()
            : snapshot ? await courseUnitsApi.reconcile(course.id, snapshot.id, operationId, "publish") : undefined;
          checked = !operation ? publicationUnknown(snapshot, fields, operationId)
            : operation.state === "committed" ? { state: "confirmed", cap: undefined, at: at(), effects: [publicationLine(true, fields)], remains: [] }
              : operation.state === "not_committed" ? { state: "refused", cap: copy.publishFailed, sentence: copy.publishNotCommitted, draft: "kept" }
                : { state: "refused", cap: copy.publishFailed, sentence: planFailureWords(operation.message), draft: "kept" };
        } catch {
          checked = publicationUnknown(snapshot, fields, operationId);
        }
        if (checked.state !== "unknown") refresh();
        setResult(checked);
        return checked;
      },
    },
  });

  const run = async (requestId: string): Promise<Result> => {
    const snapshot = unit;
    const fields = sending;
    let next: Result;
    if (mode === "publish") {
      const published = await publishPlan(snapshot);
      refresh();
      next = published.kind === "confirmed"
        ? { state: "confirmed", cap: undefined, at: at(), effects: [publicationLine(true, fields)], remains: [] }
        : published.kind === "uncertain"
          ? publicationUnknown(snapshot, fields, published.operationId)
          : { state: "refused", cap: copy.publishFailed, sentence: published.reason, draft: "kept" };
      setResult(next);
      return next;
    }
    // The roll write carries Close's key.
    return settle(await write(() => controls.current?.submitOutcome(requestId)), requestId, snapshot, fields);
  };

  /** A roll write's answer, where a refusal is said and anything unanswered is unconfirmed. */
  const write = async (send: () => Promise<RollSubmitOutcome> | undefined): Promise<RollSubmitOutcome> => {
    try {
      return (await send()) ?? { kind: "unconfirmed" };
    } catch (error) {
      return error instanceof ApiError && error.status >= 400 && error.status < 500
        ? { kind: "refused", reason: error.message }
        : { kind: "unconfirmed" };
    }
  };

  /** Close's result from a roll write's answer, under the key it was sent with: a lost answer offers Check (W1, R4). */
  const settle = async (outcome: RollSubmitOutcome, requestId: string, snapshot: CourseUnit | null, fields: string[]): Promise<Result> => {
    const publishing = mode === "close" && publish && planReady;
    let next: Result;
    if (outcome.kind === "conflict") {
      // The roll changed since this draft began: nothing commits until the person chooses (R4).
      setTheirs(outcome.theirs);
      next = { state: "none" };
    } else if (outcome.kind === "refused") {
      next = { state: "refused", cap: mode === "roll" ? copy.rollNotUpdated : copy.notClosed, sentence: outcome.reason.trim() || copy.refusedFallback, draft: "kept" };
    } else if (outcome.kind === "unconfirmed") {
      next = unconfirmed(requestId, fields, publishing);
    } else {
      setTheirs(null);
      refresh();
      const saved = outcome.roll;
      if (mode === "roll") {
        next = { state: "confirmed", cap: copy.rollUpdated(name), at: at(), effects: [...rollLines(saved), copy.staysHeld(name), copy.recapUnchanged], remains: [] };
      } else {
        const went = [copy.heldLine(name), ...rollLines(saved)];
        if (!publishing) next = { state: "confirmed", cap: copy.closed(name), at: at(), effects: [...went, publicationLine(false, fields)], remains: [copy.recordingRemains] };
        else {
          const published = await publishPlan(snapshot);
          refresh();
          next = published.kind === "confirmed"
            ? { state: "confirmed", cap: copy.closed(name), at: at(), effects: [...went, publicationLine(true, fields)], remains: [copy.recordingRemains] }
            : published.kind === "uncertain"
              ? closePublicationUnknown(snapshot, fields, went, requestId, published.operationId)
              : recapRetry(snapshot, fields, went, requestId);
        }
      }
    }
    setResult(next);
    return next;
  };

  if (session.status === "cancelled") return null;

  const read = rollRead(roll);
  const conflict = theirs !== null;
  const blockers = (): Blocker[] => {
    if (!canRecord) return [];
    if (conflict) return [{ kind: "conflict", part: "roll", sentence: copy.chooseRoll }];
    // A partial roll is valid (board 1 AGREEMENT): Close needs one mark, and the learners left unmarked stay unrecorded.
    if (mode === "close" && marked === 0 && total > 0) return [{ kind: "missing", part: "roll", sentence: copy.noneMarked }];
    if ((mode === "publish" || (mode === "close" && publish)) && planReason) return [{ kind: "missing", part: "recap", sentence: planReason }];
    return [];
  };
  const effects = (): Effect[] => {
    const summary = rollState.summary ?? "";
    if (mode === "publish") return [{ id: "publish", sentence: copy.publishTo(fieldNames(sending), total), part: "recap" }];
    const unrecorded: Effect[] = unmarked > 0 && total > 0 ? [{ id: "unrecorded", sentence: copy.leftUnrecorded(unmarked), part: "roll" }] : [];
    if (mode === "roll") return [
      { id: "roll", sentence: copy.recordRoll(summary), part: "roll" },
      ...unrecorded,
      { id: "held", sentence: copy.staysHeld(name) },
      { id: "recap", sentence: copy.recapUnchanged },
    ];
    return [
      { id: "roll", sentence: copy.recordRoll(summary), part: "roll" },
      ...unrecorded,
      { id: "held", sentence: copy.markHeld },
      ...(changed.length
        ? [{ id: "publish", sentence: copy.publishTo(fieldNames(sending), total), part: "recap", optional: { checked: publish, toggle: () => setPublishChoice(!publish) } }]
        : []),
    ];
  };
  const note = mode !== "close" ? undefined : !changed.length ? copy.noteNothing : !publish ? copy.notePrivate(fieldNames(sending)) : copy.noteDraft;

  const closedState = result.state === "unknown"
    ? { class: "reading" as const, word: copy.notConfirmed }
    : result.state === "partial" && result.unsure
      ? { class: "reading" as const, word: copy.publishUnknownCap }
    : result.state === "partial"
      ? { class: "failed" as const, word: copy.recapFailed }
      // A held session's refused send never says Held over its refusal.
      : result.state === "refused" && held
        ? { class: "failed" as const, word: mode === "publish" ? copy.recapFailed : copy.rollNotSaved }
      : conflict
        ? { class: "failed" as const, word: copy.changed }
        : held
          ? changedMarks > 0
            ? { class: "draft" as const, word: copy.rollChanges }
            : { class: "done" as const, word: copy.held }
          : marked > 0
            ? { class: "draft" as const, word: copy.marked(marked, total) }
            : session.clock_phase === "upcoming"
              ? { class: "quiet" as const, word: copy.notYet }
              : { class: "waiting" as const, word: copy.notTaken };
  const openState = !held && !conflict && result.state !== "unknown" && result.state !== "partial" && total > 0 && marked === total
    ? { class: "draft" as const, word: copy.allMarked }
    : closedState;
  const rollPartState = conflict
    ? { class: "failed" as const, word: copy.changed }
    : held && changedMarks === 0
      ? { class: "done" as const, word: copy.recorded }
      : { class: marked > 0 ? ("draft" as const) : ("waiting" as const), word: copy.notRecorded };
  const recapState = changed.some((field) => RECAP_KEYS.includes(field.key)) && recapWritten
    ? { class: "draft" as const, word: copy.written }
    : { class: "quiet" as const, word: copy.nothingWritten };
  const ahead = workspace.continuation?.foot?.next_scheduled;
  const meets = ahead?.state === "ready" ? ahead.value : null;
  const rollWhen = (attendance: SessionAttendance) => {
    const date = attendance.roll_updated_at;
    return date && Number.isFinite(Date.parse(date))
      ? ` at ${formatInCourseZone(date, course.timezone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
      : "";
  };

  const rollPart: Part = {
    id: "roll",
    label: copy.roll,
    state: rollPartState,
    markedByBox: true,
    owner: { system: "imam" },
    fold: "never",
    draft: "browser",
    render: () =>
      roll.data ? (
        <RollRegister
          key={session.id}
          roll={roll.data}
          rowsOnly
          readOnly={!canRecord}
          headingLevel="h3"
          controlsRef={controls}
          foot="none"
          participation={false}
          foldSettled={false}
          ended={session.clock_phase === "status_pending"}
          onSubmitStateChange={setRollState}
          onSubmitted={() => {
            setTheirs(null);
            refresh();
          }}
          onConflictDetected={(newer) => setTheirs(newer)}
          onConflictChoice={(choice) => {
            if (choice === "theirs") {
              // Taking the newer roll writes nothing: the draft becomes it.
              void controls.current?.resolveConflict("theirs").then((outcome) => {
                if (outcome.kind === "adopted") setTheirs(null);
                refresh();
              });
              return;
            }
            // Keeping mine is the commit, sent once under its own key, whose lost answer offers Check.
            const key = crypto.randomUUID();
            void write(() => controls.current?.resolveConflict("mine", key)).then((outcome) => settle(outcome, key, unit, sending));
          }}
          conflictMessage={(newer) => copy.rollConflict(rollWhen(newer))}
        />
      ) : null,
  };
  const recapPart: Part = {
    id: "recap",
    label: copy.recap,
    state: recapState,
    owner: { system: "imam" },
    fold: "never",
    draft: "server",
    render: () =>
      parts.unavailable ? (
        parts.notices
      ) : (
        <div className="grid gap-3">
          {(["recap", "followUp"] as const).map((key) => (
            <PlanTaskField key={key} fieldKey={key} unit={unit} state={state} sessionId={session.id} held={held} onEdit={parts.onEditField} showSaveStatus={parts.saveField === key} />
          ))}
          {/* An edit made here is answered here: a newer plan to compare, or a save to check, sits under the field that sent it. */}
          {closeOwnsEdit(parts.saveField) && <div className="empty:hidden">{parts.notices}</div>}
          {meets && (
            <p data-close-next-session className="text-meta text-muted-foreground">
              {copy.nextMeets(sessionName(meets), formatInCourseZone(meets.starts_at, meets.timezone, { weekday: "short", month: "short", day: "numeric" }))}
            </p>
          )}
        </div>
      ),
  };
  const recordingPart: Part = {
    id: "recording",
    label: copy.recording,
    state: recordingState(recordings.data),
    owner: { system: "imam" },
    fold: "choice",
    draft: "none",
    render: () => <RecordedClassJourney sessionId={session.id} classOrdinal={session.session_no} />,
  };

  const commitVerb = mode === "roll" ? copy.saveRoll : mode === "publish" ? copy.publishRecap : copy.commit;
  return {
    kind: "task",
    id: "close",
    job: copy.job,
    label: copy.label,
    owner: { system: "imam" },
    read,
    state: () => closedState,
    openState: () => openState,
    detail: (value) =>
      held
        ? `${recordedSummary(value)} · ${unit?.publication?.recap ? copy.published : copy.keptPrivate}`
        : `${copy.learners(value.roster.length)} · ${recapWritten ? copy.recapDraft : copy.recapNotWritten}`,
    act: held || !canRecord ? undefined : { verb: marked > 0 ? copy.continue : copy.take, does: "open", opens: "roll" },
    candidate: canRecord && !(held && changedMarks === 0 && changed.length === 0)
      ? { eligible: true, blocked: null, rank }
      : undefined,
    exits: held && canRecord ? [{ label: copy.update, reopens: true }] : undefined,
    fold: "settled",
    parts: [rollPart, recapPart, recordingPart],
    commit: {
      verb: commitVerb,
      cap: mode === "roll" ? copy.capUpdate : mode === "publish" ? copy.capPublish : copy.cap,
      effects,
      blockers,
      revises: held ? { part: "roll", unchanged: () => changedMarks === 0 && changed.length === 0 } : undefined,
      note,
      run,
    },
    result,
    draft: changedMarks > 0 || !!state?.dirty || (!held && marked > 0),
  };
}

function capitalise(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}
